import { Link, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ApprovalLevelChain } from 'components/ApprovalLevelChain';
import type { ChainApprover, ChainLevel } from 'components/ApprovalLevelChain/types';

import { SubjectType } from '../types';
import messages from './messages';
import { NextStep } from './NextStep';
import { useStyles } from './styles';
import { RuleGroup } from './types';

interface ApproverRailProps {
  rules: RuleGroup[];
  /** Whether the request has finished — see `levelState`. */
  requestSettled: boolean;
  /** What the request is about, for the "lost the permission" sentence. */
  subjectType?: SubjectType;
  /**
   * Opens the Replace approver dialog for an approver who left the account.
   * Absent where the rail offers no change at all, such as an edit's preview.
   */
  onUnblock?: (level: ChainLevel, approver: ChainApprover) => void;
  /**
   * Whether the reader may manage approval rules, which is what the change
   * endpoint authorizes. Without it the reader gets no control, only the next
   * step on the departed approver's name, as the queue's status cell gives it.
   */
  canManageRules?: boolean;
}

/**
 * "Unblock this level", under an approver who left the account. Only there: an
 * approver who lost the permission waits for it to be granted back, and the
 * level needs nobody to step in.
 */
const UnblockControl: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const styles = useStyles();

  return (
    <Link
      className={styles.unblockLevel}
      component="button"
      onClick={onClick}
      type="button"
      variant="overline"
    >
      <FormattedMessage {...messages.unblockLevel} />
    </Link>
  );
};

/**
 * The offer decision dialog's sidebar: "Approvers" over the level chain. The
 * heading leaves out "Offer" because the panel is already an offer.
 *
 * A heading and nothing else — the chain itself is
 * `components/ApprovalLevelChain`, shared with the recruiter read-only view (§10,
 * AXHE-3912) and AXHE-3948's progress panel. It used to render the tree here, and
 * that copy read every `skipped` level as "did not apply" — false for the sibling
 * levels a rejection closes (`Approvals::RecordDecision#close_co_active_steps`),
 * and the reason the derivation now lives in one place.
 */
export const ApproverRail: React.FC<ApproverRailProps> = ({
  rules,
  requestSettled,
  subjectType,
  onUnblock,
  canManageRules = false,
}) => {
  const styles = useStyles();
  const unblocks = onUnblock && !requestSettled;
  const renderApproverAction =
    unblocks && canManageRules
      ? (approver: ChainApprover, level: ChainLevel) =>
          approver.blockedReason === 'removed' ? (
            <UnblockControl onClick={() => onUnblock(level, approver)} />
          ) : null
      : undefined;
  const renderApproverName =
    unblocks && !canManageRules
      ? (approver: ChainApprover, _level: ChainLevel, name: React.ReactNode) =>
          approver.blockedReason === 'removed' ? (
            <NextStep step={messages.replaceApproverAskManager}>{name}</NextStep>
          ) : (
            name
          )
      : undefined;

  return (
    <div className={classNames(styles.railSection, styles.approversSection)}>
      <Typography variant="h4">
        <FormattedMessage {...messages.approversHeading} />
      </Typography>

      {/* Named only when several rules gate the offer — with one, the name is a
          heading over the whole rail that repeats what the rail already is.
          Compact and identity-only because the design draws each approver as
          a name and an answer, with no address and no pill or date beside a
          level's name; each approver's date stays. A backup keeps its tag, so
          "N of M approved" adds up over an escalated level's names. */}
      <ApprovalLevelChain
        compact
        identityOnly
        renderApproverAction={renderApproverAction}
        renderApproverName={renderApproverName}
        rules={rules}
        requestSettled={requestSettled}
        showRuleNames={rules.length > 1}
        subjectType={subjectType}
        tagBackups
      />
    </div>
  );
};
